import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Sparkles, Crown } from "lucide-react";
import { LEVELS } from "@/data/levels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function YourBest({ name }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!name) {
      setLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/me/${encodeURIComponent(name)}`);
        if (!mounted) return;
        setStats(res.data);
      } catch (e) {
        console.error("YourBest fetch failed", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [name]);

  if (!name) {
    return (
      <section
        className="rounded-3xl border-[4px] border-dashed border-tiger-deep/40 bg-tiger-cream/60 p-6 text-center shadow-sticker"
        data-testid="your-best-empty"
      >
        <p className="font-heading text-xl font-bold text-tiger-deep">
          🐯 Take the quiz or play a level to unlock your personal best!
        </p>
      </section>
    );
  }

  if (loading) {
    return (
      <div
        className="rounded-3xl border-[4px] border-tiger-deep bg-white p-6 shadow-stickerLg"
        data-testid="your-best-loading"
      >
        <p className="font-body text-base font-bold text-tiger-deep/60">
          Loading {name}'s best…
        </p>
      </div>
    );
  }

  if (!stats) return null;

  const maxStars = LEVELS.length * 3;
  const starPct = Math.min(100, (stats.game_total_stars / maxStars) * 100);
  const hasAnyData =
    stats.quiz_attempts > 0 || stats.game_attempts > 0;

  return (
    <section
      className="relative overflow-hidden rounded-3xl border-[4px] border-tiger-deep bg-tiger-deep p-6 sm:p-8 shadow-stickerLg text-tiger-sand"
      data-testid="your-best-card"
    >
      <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-tiger-orange/30 blur-3xl" />
      <div className="pointer-events-none absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-jungle-leaf/30 blur-3xl" />

      <div className="relative flex flex-wrap items-center gap-4">
        <span className="grid h-14 w-14 place-items-center rounded-2xl border-[3px] border-tiger-sand bg-tiger-orange shadow-sticker">
          <Crown className="h-7 w-7 text-white" />
        </span>
        <div>
          <p className="font-heading text-xs font-bold uppercase tracking-widest text-sun">
            Your personal best
          </p>
          <h3 className="font-heading text-3xl font-bold text-tiger-sand sm:text-4xl">
            {name}&apos;s Hero Card
          </h3>
        </div>
      </div>

      {!hasAnyData && (
        <p
          className="relative mt-5 rounded-2xl border-[3px] border-tiger-sand/30 bg-white/10 px-4 py-3 font-body text-sm font-bold text-tiger-sand"
          data-testid="your-best-no-data"
        >
          No saved scores yet — take the quiz or finish a game level to fill
          this card!
        </p>
      )}

      <div className="relative mt-6 grid gap-4 sm:grid-cols-3">
        <Stat
          label="Quiz best"
          value={
            stats.quiz_best != null
              ? `${stats.quiz_best}/${stats.quiz_total}`
              : "—"
          }
          sub={
            stats.quiz_attempts > 0
              ? `${stats.quiz_attempts} attempt${stats.quiz_attempts === 1 ? "" : "s"}`
              : "Not taken yet"
          }
          color="bg-jungle-leaf"
          testid="stat-quiz"
        />
        <Stat
          label="Game high score"
          value={stats.game_best_score || "—"}
          sub={
            stats.game_attempts > 0
              ? `${stats.game_attempts} run${stats.game_attempts === 1 ? "" : "s"}`
              : "Not played yet"
          }
          color="bg-tiger-orange"
          testid="stat-game-score"
        />
        <Stat
          label="Levels conquered"
          value={`${stats.game_levels_completed} / ${LEVELS.length}`}
          sub={`${stats.game_total_stars} / ${maxStars} stars earned`}
          color="bg-sun"
          colorText="text-tiger-deep"
          testid="stat-levels"
        />
      </div>

      <div className="relative mt-6">
        <div className="flex items-center justify-between font-body text-sm font-bold text-tiger-sand">
          <span className="inline-flex items-center gap-1">
            <Sparkles className="h-4 w-4 text-sun" /> Star progress
          </span>
          <span data-testid="your-best-stars">
            {stats.game_total_stars} / {maxStars}
          </span>
        </div>
        <div className="mt-2 h-3 w-full overflow-hidden rounded-full border-[2px] border-tiger-sand bg-tiger-sand/15">
          <div
            className="h-full bg-sun transition-all duration-500"
            style={{ width: `${starPct}%` }}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {LEVELS.map((lvl, i) => {
            const earned = i < stats.game_levels_completed;
            return (
              <span
                key={lvl.id}
                title={lvl.name}
                className={`grid h-9 w-9 place-items-center rounded-xl border-[3px] text-base ${
                  earned
                    ? "border-sun bg-sun text-tiger-deep"
                    : "border-tiger-sand/30 bg-white/10 text-tiger-sand/50"
                }`}
              >
                {earned ? <Star className="h-4 w-4 fill-tiger-deep" /> : lvl.emoji}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, sub, color, colorText = "text-white", testid }) {
  return (
    <div
      className={`rounded-2xl border-[3px] border-tiger-sand ${color} ${colorText} px-4 py-3 shadow-sticker`}
      data-testid={testid}
    >
      <p className="font-heading text-xs font-bold uppercase tracking-widest opacity-80">
        {label}
      </p>
      <p className="mt-1 font-heading text-3xl font-bold leading-none">
        {value}
      </p>
      <p className="mt-1 font-body text-xs opacity-80">{sub}</p>
    </div>
  );
}
